/**
 * Network fallback — the switch on the Organisation page (migration 082;
 * rules: server/src/services/network-fallback.ts, plan:
 * specs/012-widget-partner-portal/decisions/fallback-network.md).
 *
 * When nobody on the partner's side can take a request, FinderlyFix can pass
 * it to a contractor network. The homeowner still ticks a separate, unticked
 * box in the widget before anything is shared with that network; this switch
 * only decides whether the widget offers it. Partner Terms §6.9.
 *
 * The flag is per site (`widget_sites.finderly_network_fallback`), so the card
 * shows the site selected in the top bar and says so. Switching it writes the
 * site's live and test keys.
 *
 * What the card offers depends on the organisation's partner type (the
 * server's `access`):
 *
 *   self_serve   an On/Off switch for owners and admins, the disclosure
 *                sentence next to it, a confirm step to turn it on; off is
 *                immediate;
 *   review       (`other`) "Request to turn on" — FinderlyFix checks network
 *                fit; the request can be cancelled; off is immediate;
 *   unavailable  (warranty, insurer, membership) no switch: a neutral network
 *                isn't available yet, and they can ask to be told.
 *
 * `/setup/fallback` redirects here (#network-fallback).
 */
import { useEffect, useRef, useState } from 'react';
import { can } from '../auth/roles';
import { useSession } from '../auth/session';
import type { NetworkFallbackState } from '../api/types';
import { Link } from '../router';
import { useSites } from '../state/sites';
import { ErrorNotice, Modal, Switch, formatDateTime, useToast } from '../ui/kit';
import { useMutation, useQuery } from '../ui/hooks';
import { PARTNER_TYPE_COPY } from './OrgSetup';

export const NETWORK_FALLBACK_ANCHOR = 'network-fallback';

/** The partner-facing words for the network. */
export function networkName(state: Pick<NetworkFallbackState, 'network'>): string {
  return state.network === 'elocal' ? 'eLocal' : 'a neutral network';
}

function typeLabel(type: string): string {
  return (PARTNER_TYPE_COPY as Record<string, { label: string } | undefined>)[type]?.label ?? type;
}

export function NetworkFallbackRow() {
  const { api, org, role } = useSession();
  const { current, pairs } = useSites();
  const toast = useToast();
  const key = current?.live.site_key ?? '';
  const allowed = can(role, 'sites');
  const editable = can(role, 'editOrg');
  // Re-read when the partner type changes: the network and the rules follow it.
  const state = useQuery(() => api.getNetworkFallback(key), ['network-fallback', key, org?.partner_type], {
    enabled: allowed && Boolean(key),
  });
  const ref = useRef<HTMLElement>(null);
  const [confirming, setConfirming] = useState<'on' | 'request' | null>(null);
  const [note, setNote] = useState('');
  // Arriving from an old /setup/fallback link: bring the row into view.
  useEffect(() => {
    if (window.location.hash === `#${NETWORK_FALLBACK_ANCHOR}`) {
      ref.current?.scrollIntoView?.({ block: 'center' });
      ref.current?.focus({ preventScroll: true });
    }
  }, []);

  const set = useMutation((enabled: boolean) => api.setNetworkFallback(key, enabled));
  const ask = useMutation((text: string) => api.requestNetworkFallback(key, text.trim() || undefined));
  const cancel = useMutation(() => api.cancelNetworkFallbackRequest(key));
  const notify = useMutation(() => api.notifyNeutralNetwork());
  const unnotify = useMutation(() => api.cancelNeutralNetworkNotify());

  if (!allowed || !key) return null;

  const s = state.data;
  const busy = set.pending || ask.pending || cancel.pending || notify.pending || unnotify.pending;
  // The dialogs show their own error; the card shows the rest.
  const cardError = (confirming ? null : (set.error ?? ask.error)) ?? cancel.error ?? notify.error ?? unnotify.error ?? state.error;

  const turnOff = async () => {
    const next = await set.run(false);
    if (next) {
      state.setData(next);
      toast(`Network fallback turned off for ${next.label}`);
    }
  };
  const confirmOn = async () => {
    const next = await set.run(true);
    if (next) {
      state.setData(next);
      setConfirming(null);
      toast(`Network fallback turned on for ${next.label}`);
    }
  };
  const sendRequest = async () => {
    const next = await ask.run(note);
    if (next) {
      state.setData(next);
      setConfirming(null);
      setNote('');
      toast('Request sent to FinderlyFix');
    }
  };
  const cancelRequest = async () => {
    await cancel.run();
    await state.reload();
  };
  const askToBeTold = async () => {
    if (await notify.run()) await state.reload();
  };
  const stopTelling = async () => {
    await unnotify.run();
    await state.reload();
  };

  const pending = s?.request?.status === 'pending' ? s.request : null;
  const declined = s && !s.enabled && s.request?.status === 'declined' ? s.request : null;
  const siteName = s?.label ?? current?.live.label ?? '';

  return (
    <section id={NETWORK_FALLBACK_ANCHOR} ref={ref} tabIndex={-1} className="panel panel-pad stack-sm" aria-labelledby="fallback-h">
      <div className="row-between">
        <h2 id="fallback-h" className="h-sub">
          Network fallback
        </h2>
        {s ? (
          <span className={`tag ${s.enabled ? 'accent' : pending ? 'warn' : ''}`}>{s.enabled ? 'On' : pending ? 'Requested' : 'Off'}</span>
        ) : null}
      </div>
      <p className="small muted" style={{ margin: 0 }}>
        Applies to the site <strong>{siteName}</strong> (its live and test keys)
        {pairs.length > 1 ? '. Each site has its own setting; switch sites in the top bar.' : '.'}
      </p>
      <ErrorNotice error={cardError} />
      {s && s.access === 'unavailable' ? (
        <>
          <p className="small" style={{ margin: 0 }}>
            <strong>A neutral network isn’t available yet, so this stays off.</strong> For organisations of type “{typeLabel(s.partner_type)}”, we
            don’t use eLocal (majority-owned by HomeServe), and we haven’t connected an independent network yet.
          </p>
          {s.enabled && editable ? (
            <Switch
              label={`Network fallback for ${s.label}`}
              description="On. Turning it off takes effect at once."
              checked
              disabled={busy}
              onChange={() => void turnOff()}
            />
          ) : null}
          {!editable ? (
            <p className="small muted" style={{ margin: 0 }}>
              Only owners and admins can change this.
            </p>
          ) : s.notify_requested ? (
            <p className="small" style={{ margin: 0 }}>
              FinderlyFix will let you know when a neutral network is available.{' '}
              <button type="button" className="btn-link" onClick={() => void stopTelling()} disabled={busy}>
                Don’t notify me
              </button>
            </p>
          ) : (
            <div>
              <button type="button" className="btn btn-sm" onClick={() => void askToBeTold()} disabled={busy}>
                Notify me when it’s available
              </button>
            </div>
          )}
        </>
      ) : s ? (
        <>
          <p className="small" style={{ margin: 0 }}>
            When nobody on your team can take a homeowner’s request, we can pass it to {networkName(s)}. The homeowner ticks a separate box before
            anything is shared with them.
          </p>
          <p className="small fallback-disclosure">{s.disclosure}</p>
          {editable && (s.access === 'self_serve' || s.enabled) ? (
            <Switch
              label={`Network fallback for ${s.label}`}
              description={s.enabled ? 'On. Turning it off takes effect at once.' : 'Off. You’ll confirm before it turns on.'}
              checked={s.enabled}
              disabled={busy}
              onChange={(on) => (on ? setConfirming('on') : void turnOff())}
            />
          ) : null}
          {s.access === 'review' && !s.enabled ? (
            pending ? (
              <div className="stack-sm">
                <p className="small" style={{ margin: 0 }} role="status">
                  <strong>Requested — FinderlyFix is checking your network fit.</strong> Sent {formatDateTime(pending.created_at)}
                  {pending.requested_by_email ? ` by ${pending.requested_by_email}` : ''}.
                </p>
                {editable ? (
                  <div>
                    <button type="button" className="btn btn-sm" onClick={() => void cancelRequest()} disabled={busy}>
                      {cancel.pending ? 'Cancelling…' : 'Cancel request'}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="stack-sm">
                <p className="small muted" style={{ margin: 0 }}>
                  For organisations of type “{typeLabel(s.partner_type)}”, FinderlyFix first checks that you don’t compete with HomeServe (which
                  majority-owns eLocal), for example by selling home warranties.
                </p>
                {declined ? (
                  <p className="small" style={{ margin: 0 }}>
                    FinderlyFix declined your last request{declined.decided_at ? ` on ${formatDateTime(declined.decided_at)}` : ''}
                    {declined.decision_reason ? `: “${declined.decision_reason}”` : '.'}
                  </p>
                ) : null}
                {editable ? (
                  <div>
                    <button type="button" className="btn btn-sm btn-primary" onClick={() => setConfirming('request')} disabled={busy}>
                      Request to turn on
                    </button>
                  </div>
                ) : null}
              </div>
            )
          ) : null}
          {s.pending_agent_change && !s.enabled ? (
            <p className="small" style={{ margin: 0 }}>
              An AI agent asked to turn this on
              {s.pending_agent_change.applies_at ? <> (it takes effect {formatDateTime(s.pending_agent_change.applies_at)})</> : null}. Approve or
              reject it at the top of your <Link to="/dashboard">dashboard</Link>.
            </p>
          ) : null}
          {!editable ? (
            <p className="small muted" style={{ margin: 0 }}>
              Only owners and admins can change this.
            </p>
          ) : null}
        </>
      ) : null}
      {s && confirming === 'on' ? (
        <Modal
          open
          title={`Turn on the network fallback for ${s.label}?`}
          onClose={() => setConfirming(null)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setConfirming(null)} disabled={set.pending}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={() => void confirmOn()} disabled={set.pending}>
                {set.pending ? 'Turning on…' : 'Turn on'}
              </button>
            </>
          }
        >
          <div className="stack-sm">
            <p style={{ margin: 0 }}>{s.disclosure}</p>
            <p className="small muted" style={{ margin: 0 }}>
              It applies to {s.label} (live and test keys). Homeowners still choose, with a separate unticked box, whether their request is shared
              with {networkName(s)}.
            </p>
            <ErrorNotice error={set.error} />
          </div>
        </Modal>
      ) : null}
      {s && confirming === 'request' ? (
        <Modal
          open
          title={`Ask FinderlyFix to turn on the network fallback for ${s.label}?`}
          onClose={() => setConfirming(null)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setConfirming(null)} disabled={ask.pending}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={() => void sendRequest()} disabled={ask.pending}>
                {ask.pending ? 'Sending…' : 'Send request'}
              </button>
            </>
          }
        >
          <div className="stack-sm">
            <p style={{ margin: 0 }}>{s.disclosure}</p>
            <p className="small muted" style={{ margin: 0 }}>
              FinderlyFix checks your network fit, then turns it on or tells you why not. You can cancel the request until then.
            </p>
            <div className="field">
              <label htmlFor="fallback-note">Anything we should know (optional)</label>
              <textarea
                id="fallback-note"
                className="input"
                rows={3}
                maxLength={500}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. we are a handyman marketplace and sell no warranty or insurance products"
              />
            </div>
            <ErrorNotice error={ask.error} />
          </div>
        </Modal>
      ) : null}
    </section>
  );
}
